import { UW_SCORING_ROW_SPECS, indexScorecardMetricsByKey } from './underwritingScorecardScoring';

export const UNDERWRITING_TEMPLATE_URL =
  '/templates/underwriting-calculator-template.xlsx';
export const UNDERWRITING_TEMPLATE_SHEET_NAME = 'Underwriting Calculator';

const RATING_ROWS = Array.from({ length: 12 }, (_, index) => 10 + index);
const UNDERWRITING_SHEET_LAST_CONTENT_ROW = 31;
const LEGEND_EXPLANATION_HEADER = 'Explanation of calculation';
export const RUNOFF_EXPLANATION =
  '12 month predicted runoff / monthly OPEX (months of OPEX covered)';
export const RUNWAY_EXPLANATION =
  'Beginning cash ÷ net burn (average absolute monthly cash change over the last 4 months)';
export const LIQUID_VALUE_EXPLANATION =
  'Beginning cash + 12 month predicted runoff − debt, divided by monthly OPEX';
export const CAC_PAYBACK_EXPLANATION =
  'First month (1-indexed) cumulative generic ROAS reaches 100%';
export const ROAS_VECTOR_EXPLANATION =
  'First month (1-indexed) cumulative generic ROAS reaches 50%';
export const UA_PREDICTION_EXPLANATION =
  'Hill model: max marketing spend with 100% cumulative ROAS at the latest available MOB (capped at 12) ÷ reference marketing spend';
export const REVENUE_GROWTH_YOY_EXPLANATION =
  '(Revenue last full month − revenue same month prior year) ÷ revenue same month prior year × 100';
export const BALANCE_SHEET_EXPLANATION =
  'Total assets ÷ (total liabilities − SAFE)';
export const CASH_BALANCE_EXPLANATION =
  'Beginning cash ÷ monthly OPEX';

const METRIC_EXPLANATIONS_BY_ROW = {
  10: RUNWAY_EXPLANATION,
  11: RUNOFF_EXPLANATION,
  12: LIQUID_VALUE_EXPLANATION,
  13: REVENUE_GROWTH_YOY_EXPLANATION,
  14: BALANCE_SHEET_EXPLANATION,
  16: CASH_BALANCE_EXPLANATION,
  17: CAC_PAYBACK_EXPLANATION,
  18: ROAS_VECTOR_EXPLANATION,
  19: UA_PREDICTION_EXPLANATION,
};

export { METRIC_EXPLANATIONS_BY_ROW };

const SCORE_FORMULA =
  'IF(E{row}="High",3,IF(E{row}="Moderate",2,IF(E{row}="Low",1,"")))';
const WEIGHT_FORMULA =
  'IF(E{row}="","",IF(COUNTA($E$10:$E$21)=0,"",100/COUNTA($E$10:$E$21)))';
const WEIGHTED_SCORE_FORMULA =
  'IF(F{row}="","",F{row}*G{row}/100)';

/** Matches template Weight (%) column display (two decimal places). */
export const WEIGHT_PERCENT_NUM_FMT = '0.00';

function applyWeightPercentColumnFormat(worksheet, address, options = {}) {
  const { wrapText = false } = options;
  const referenceCell = worksheet.getCell('G10');
  const cell = worksheet.getCell(address);
  if (referenceCell) {
    copyCellStyle(referenceCell, cell);
  }
  cell.numFmt = WEIGHT_PERCENT_NUM_FMT;
  if (wrapText) {
    cell.alignment = {
      ...(cell.alignment ?? {}),
      wrapText: true,
      vertical: 'top',
    };
  }
}

function applyUnderwritingScoringColumnFormats(worksheet) {
  RATING_ROWS.forEach((rowNumber) => {
    applyWeightPercentColumnFormat(worksheet, `G${rowNumber}`);
    applyWeightPercentColumnFormat(worksheet, `H${rowNumber}`);
    applyWeightPercentColumnFormat(worksheet, `I${rowNumber}`, {
      wrapText: true,
    });
  });
  applyWeightPercentColumnFormat(worksheet, 'G22');
  applyWeightPercentColumnFormat(worksheet, 'H22');
}

let templateBufferCache = null;

export function resetUnderwritingTemplateCache() {
  templateBufferCache = null;
}

export async function fetchUnderwritingTemplateBuffer(
  fetchImpl = global.fetch
) {
  if (templateBufferCache) {
    return templateBufferCache;
  }
  const response = await fetchImpl(UNDERWRITING_TEMPLATE_URL);
  if (!response.ok) {
    throw new Error(
      `Failed to load underwriting template (${response.status})`
    );
  }
  templateBufferCache = await response.arrayBuffer();
  return templateBufferCache;
}

export async function loadUnderwritingCalculatorWorksheet(
  ExcelJS,
  buffer
) {
  const templateWorkbook = new ExcelJS.Workbook();
  await templateWorkbook.xlsx.load(buffer);
  const worksheet = templateWorkbook.getWorksheet(
    UNDERWRITING_TEMPLATE_SHEET_NAME
  );
  if (!worksheet) {
    throw new Error('Underwriting template sheet not found');
  }
  clearUnderwritingAnalystFields(worksheet);
  return worksheet;
}

export function clearUnderwritingAnalystFields(worksheet) {
  worksheet.getCell('F6').value = null;
  RATING_ROWS.forEach((rowNumber) => {
    worksheet.getCell(`D${rowNumber}`).value = null;
    worksheet.getCell(`E${rowNumber}`).value = null;
    worksheet.getCell(`I${rowNumber}`).value = null;
    worksheet.getCell(`L${rowNumber}`).value = null;
    worksheet.getCell(`M${rowNumber}`).value = null;
    worksheet.getCell(`N${rowNumber}`).value = null;
    worksheet.getCell(`O${rowNumber}`).value = null;
    worksheet.getCell(`P${rowNumber}`).value = null;
  });
}

function copyCellValue(sourceCell, targetCell) {
  if (sourceCell.formula) {
    targetCell.value = {
      formula: sourceCell.formula,
      result: sourceCell.result,
    };
    return;
  }
  targetCell.value = sourceCell.value;
}

function copyCellStyle(sourceCell, targetCell) {
  if (sourceCell.style) {
    targetCell.style = { ...sourceCell.style };
  }
  if (sourceCell.numFmt) {
    targetCell.numFmt = sourceCell.numFmt;
  }
}

function copyLegendHeaderStyle(sourceWorksheet, targetCell) {
  const referenceCell = sourceWorksheet.getCell('M9');
  copyCellStyle(referenceCell, targetCell);
}

export function applyUnderwritingScoringFormulas(worksheet) {
  worksheet.getCell('B9').value = '#';
  worksheet.getCell('C9').value = 'Metric';
  worksheet.getCell('D9').value = 'Value';
  worksheet.getCell('E9').value = 'Rating';
  worksheet.getCell('F9').value = 'Score';
  worksheet.getCell('G9').value = 'Weight (%)';
  worksheet.getCell('H9').value = 'Weighted Score';
  worksheet.getCell('I9').value = 'Calculation inputs';

  RATING_ROWS.forEach((rowNumber) => {
    worksheet.getCell(`F${rowNumber}`).value = {
      formula: SCORE_FORMULA.replace(/{row}/g, String(rowNumber)),
    };
    worksheet.getCell(`G${rowNumber}`).value = {
      formula: WEIGHT_FORMULA.replace(/{row}/g, String(rowNumber)),
    };
    worksheet.getCell(`H${rowNumber}`).value = {
      formula: WEIGHTED_SCORE_FORMULA.replace(/{row}/g, String(rowNumber)),
    };
    worksheet.getCell(`I${rowNumber}`).value = null;
  });

  worksheet.getCell('D22').value = {
    formula: 'COUNTA(E10:E21)&" / 12 rated"',
  };
  worksheet.getCell('F22').value = null;
  worksheet.getCell('G22').value = { formula: 'SUM(G10:G21)' };
  worksheet.getCell('H22').value = { formula: 'SUM(H10:H21)' };

  worksheet.getCell('D25').value = {
    formula: 'SUMIF(F10:F21,">0",G10:G21)*3/100',
  };
  worksheet.getCell('D26').value = { formula: 'H22' };
  worksheet.getCell('D27').value = { formula: 'IF(D26=0,"",D26/D25)' };
  worksheet.getCell('D28').value = {
    formula:
      'IF(D27="","Enter Ratings",IF(D27>=0.75,"HIGH CONVICTION",IF(D27>=0.5,"MODERATE","LOW / PASS")))',
  };

  applyUnderwritingScoringColumnFormats(worksheet);
}

export function applyUnderwritingSheetLayout(worksheet, sourceWorksheet) {
  worksheet.getCell('B7').value = 'Monthly OPEX $';
  worksheet.getCell('B7').font = { bold: true };

  const explanationHeader = worksheet.getCell('P9');
  explanationHeader.value = LEGEND_EXPLANATION_HEADER;
  if (sourceWorksheet) {
    copyLegendHeaderStyle(sourceWorksheet, explanationHeader);
  }

  applyUnderwritingScoringFormulas(worksheet);
  worksheet.getColumn(16).width = 42;
}

export function populateUnderwritingLegendBands(worksheet, scorecardConfig) {
  if (!worksheet || !scorecardConfig?.metrics?.length) {
    return;
  }
  const metricsByKey = indexScorecardMetricsByKey(scorecardConfig);
  UW_SCORING_ROW_SPECS.forEach(({ row, key }) => {
    const metric = metricsByKey.get(key);
    if (!metric) {
      return;
    }
    worksheet.getCell(`L${row}`).value = metric.label ?? null;
    worksheet.getCell(`M${row}`).value = metric.labelScore3 ?? null;
    worksheet.getCell(`N${row}`).value = metric.labelScore2 ?? null;
    worksheet.getCell(`O${row}`).value = metric.labelScore1 ?? null;
  });
}

export function populateUnderwritingMetricExplanations(worksheet) {
  if (!worksheet) {
    return;
  }
  Object.entries(METRIC_EXPLANATIONS_BY_ROW).forEach(([row, explanation]) => {
    worksheet.getCell(`P${row}`).value = explanation;
  });
}

export function populateUnderwritingScoringTable(worksheet, scoringRows) {
  if (!worksheet || !scoringRows?.length) {
    return;
  }
  scoringRows.forEach((row) => {
    if (row.valueText) {
      worksheet.getCell(`D${row.row}`).value = row.valueText;
    }
    if (row.rating) {
      worksheet.getCell(`E${row.row}`).value = row.rating;
    }
    if (row.paramsText) {
      const cell = worksheet.getCell(`I${row.row}`);
      cell.value = row.paramsText;
      applyWeightPercentColumnFormat(worksheet, `I${row.row}`, {
        wrapText: true,
      });
    }
  });
}

export function populateUnderwritingMonthlyOpex(worksheet, monthlyOpex) {
  if (!worksheet || monthlyOpex == null || Number(monthlyOpex) <= 0) {
    return;
  }
  worksheet.getCell('F7').value = Number(monthlyOpex);
  worksheet.getCell('F7').numFmt = '$#,##0;[Red]($#,##0)';
}

export function populateUnderwritingCompanyName(worksheet, companyName) {
  if (!worksheet) {
    return;
  }
  const name = String(companyName ?? '').trim();
  if (!name) {
    return;
  }
  worksheet.getCell('F6').value = name;
}

/** @deprecated use populateUnderwritingScoringTable */
export function populateUnderwritingCalculatedValues(
  worksheet,
  calculatedValues
) {
  populateUnderwritingMonthlyOpex(worksheet, calculatedValues?.monthlyOpex);
  if (calculatedValues?.runoffEstimatedValue) {
    worksheet.getCell('D11').value = calculatedValues.runoffEstimatedValue;
  }
  if (calculatedValues?.cacPaybackEstimatedValue) {
    worksheet.getCell('D17').value = calculatedValues.cacPaybackEstimatedValue;
  }
}

export function cloneWorksheet(sourceWorksheet, targetWorkbook, sheetName) {
  const targetWorksheet = targetWorkbook.addWorksheet(sheetName, {
    properties: sourceWorksheet.properties,
    pageSetup: sourceWorksheet.pageSetup,
    headerFooter: sourceWorksheet.headerFooter,
    views: sourceWorksheet.views,
  });

  sourceWorksheet.columns?.forEach((column, index) => {
    if (!column) {
      return;
    }
    const targetColumn = targetWorksheet.getColumn(index + 1);
    if (column.width != null) {
      targetColumn.width = column.width;
    }
    if (column.hidden != null) {
      targetColumn.hidden = column.hidden;
    }
  });

  sourceWorksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const targetRow = targetWorksheet.getRow(rowNumber);
    if (row.height != null) {
      targetRow.height = row.height;
    }
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const targetCell = targetRow.getCell(colNumber);
      copyCellValue(cell, targetCell);
      copyCellStyle(cell, targetCell);
    });
    targetRow.commit();
  });

  const merges = sourceWorksheet.model?.merges ?? [];
  merges.forEach((mergeRange) => {
    targetWorksheet.mergeCells(mergeRange);
  });

  return targetWorksheet;
}

export function trimUnderwritingSheetFooter(worksheet) {
  if (!worksheet) {
    return;
  }
  const lastRow = worksheet.rowCount ?? UNDERWRITING_SHEET_LAST_CONTENT_ROW;
  for (
    let rowNumber = UNDERWRITING_SHEET_LAST_CONTENT_ROW + 1;
    rowNumber <= lastRow;
    rowNumber += 1
  ) {
    const row = worksheet.getRow(rowNumber);
    row.values = [];
    row.commit();
  }
}

export async function appendUnderwritingCalculatorSheet(
  workbook,
  ExcelJS,
  options = {}
) {
  const fetchImpl = options.fetchImpl ?? global.fetch;
  const buffer =
    options.templateBuffer ??
    (await fetchUnderwritingTemplateBuffer(fetchImpl));
  const sourceWorksheet = await loadUnderwritingCalculatorWorksheet(
    ExcelJS,
    buffer
  );
  const cloned = cloneWorksheet(
    sourceWorksheet,
    workbook,
    UNDERWRITING_TEMPLATE_SHEET_NAME
  );
  applyUnderwritingSheetLayout(cloned, sourceWorksheet);
  populateUnderwritingMetricExplanations(cloned);
  populateUnderwritingLegendBands(cloned, options.scorecardConfig);
  populateUnderwritingScoringTable(cloned, options.scoringRows);
  populateUnderwritingCompanyName(cloned, options.companyName);
  populateUnderwritingMonthlyOpex(cloned, options.monthlyOpex);
  trimUnderwritingSheetFooter(cloned);

  return cloned;
}
